/* ============================================================================
   Sero Candles Set Builder: minimal, mobile-first engine
   ----------------------------------------------------------------------------
   Reads the single-source config from sections/set-builder.liquid (which reads
   the `sero.pricing` shop metafield, One Price Everywhere), renders the
   tap-to-add builder, and writes REAL component line items to /cart/add.js.
   Pricing mirrors pricing.rs in the discount function EXACTLY: any basket is
   priced at the cheapest combination of preset atoms (packs/sets) + singles,
   so UI = checkout. Unlocked gifts (matches / bonus candle) ride along as
   REAL lines tagged `_set_gift`; the function zeroes them at checkout.
   On add we open the theme cart drawer via the theme's window.refreshCart().
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- GA4 sb_* events (D3 analytics) ----------------------------
     Page views stay owned by the Shopify Google channel pixel; this loads
     gtag ONLY for sb_* events (send_page_view:false, no duplicate traffic).
     Events also publish to Shopify.analytics for future custom pixels.
     Analytics must never break the builder: everything is try/caught. */
  const GA4_ID = 'G-G37JG5CQNZ';
  function sbGtag() {
    if (window.gtag) return window.gtag;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, { send_page_view: false });
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA4_ID;
    document.head.appendChild(s);
    return window.gtag;
  }
  function sbTrack(name, params) {
    try {
      sbGtag()('event', name, params || {});
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function')
        window.Shopify.analytics.publish(name, params || {});
    } catch (e) { /* never block the builder on analytics */ }
  }
  if (!window.sbTrack) window.sbTrack = sbTrack; // shared with the modal host

  /* ---------- One Price Everywhere: preset-combination engine ----------
     MIRRORS pricing.rs in the discount function (same atoms, same DP, same
     fallback) so the panel total always equals the checkout total. */
  const FALLBACK_PRICING = {
    candle_price_cents: 2400, container_price_cents: 5900,
    atoms: [
      { containers: 0, candles: 3, price_cents: 5900, label: '3-Pack' },
      { containers: 0, candles: 6, price_cents: 10900, label: '6-Pack' },
      { containers: 0, candles: 12, price_cents: 19900, label: '12-Pack' },
      { containers: 1, candles: 2, price_cents: 8900, label: 'Starter Set' },
      { containers: 2, candles: 4, price_cents: 16900, label: 'Multi-Room Set' },
      { containers: 2, candles: 10, price_cents: 25900, label: 'Every-Room Set' }
    ],
    gifts: {
      matches: { threshold_cents: 8500, max_qty: 1, message: 'Free vintage matches' },
      bonus_candle: { threshold_cents: 18500, max_qty: 1, message: 'Free bonus candle' }
    },
    free_shipping_threshold_cents: 25000
  };
  function normalizePricing(p) {
    return (p && Array.isArray(p.atoms) && p.atoms.length && p.candle_price_cents > 0) ? p : FALLBACK_PRICING;
  }
  const REWARD_META = {
    matches: { image: 'https://cdn.shopify.com/s/files/1/0752/6517/9929/files/vintage-matches.webp' },
    bonus_candle: { image: 'https://cdn.shopify.com/s/files/1/0752/6517/9929/files/Sero_Candles_Uplift_Recyclable_6.5_oz_Candle_Square.png' }
  };
  function buildRewards(p, giftCfg) {
    const out = [];
    Object.keys(p.gifts || {}).forEach(kind => {
      const g = p.gifts[kind];
      if (!g || !(g.max_qty > 0) || !(g.threshold_cents > 0)) return;
      const cfg = giftCfg[kind] || {};
      if (cfg.available === false) return;
      out.push({ kind, threshold: g.threshold_cents, label: g.message || 'Free bonus', image: (REWARD_META[kind] || {}).image || '' });
    });
    // DISPLAY-ONLY rung. Free ground shipping at $50 is a Shopify DELIVERY RATE
    // ("Ground Shipping" $0.00, condition TOTAL_PRICE >= $50), NOT the discount
    // function. It is deliberately kept out of sero.pricing so the function is
    // never handed a reward it cannot deliver. If the delivery rate's threshold
    // changes in Shopify Admin, change FREE_GROUND_CENTS to match.
    const FREE_GROUND_CENTS = 5000;
    out.push({ kind: 'free_ground', threshold: FREE_GROUND_CENTS, label: 'Free shipping', image: '' });
    if (p.free_shipping_threshold_cents > 0)
      out.push({ kind: 'free_shipping', threshold: p.free_shipping_threshold_cents, label: 'Free expedited shipping', image: '' });
    out.sort((a, b) => a.threshold - b.threshold);
    return out;
  }
  function bestPrice(p, containers, candles) {
    let c = Math.max(0, containers | 0), k = Math.max(0, candles | 0), base = 0;
    const baseAtoms = [];
    const bulk = p.atoms.filter(a => a.containers === 0 && a.candles > 0).sort((a, b) => b.candles - a.candles)[0];
    while (k > 120 && bulk) { base += bulk.price_cents; baseAtoms.push(bulk.label || 'Pack'); k -= bulk.candles; }
    while (c > 40) { base += p.container_price_cents; c -= 1; }
    const W = k + 1, INF = Infinity;
    const dp = new Array((c + 1) * W).fill(INF);
    const from = new Array((c + 1) * W).fill(-1); // -2 container, -3 candle, >=0 atom index
    dp[0] = 0;
    for (let ci = 0; ci <= c; ci++) for (let ki = 0; ki <= k; ki++) {
      const i = ci * W + ki, cur = dp[i];
      if (cur === INF) continue;
      if (ci + 1 <= c) { const j = (ci + 1) * W + ki, v = cur + p.container_price_cents; if (v < dp[j]) { dp[j] = v; from[j] = -2; } }
      if (ki + 1 <= k) { const j = ci * W + ki + 1, v = cur + p.candle_price_cents; if (v < dp[j]) { dp[j] = v; from[j] = -3; } }
      for (let ai = 0; ai < p.atoms.length; ai++) {
        const a = p.atoms[ai], nc = ci + a.containers, nk = ki + a.candles;
        if (nc <= c && nk <= k) { const j = nc * W + nk, v = cur + a.price_cents; if (v < dp[j]) { dp[j] = v; from[j] = ai; } }
      }
    }
    const atoms = baseAtoms.slice(); let singles = 0, ci = c, ki = k;
    while (ci > 0 || ki > 0) {
      const m = from[ci * W + ki];
      if (m === -2) { singles++; ci -= 1; }
      else if (m === -3) { singles++; ki -= 1; }
      else if (m >= 0) { const a = p.atoms[m]; atoms.push(a.label || 'Bundle'); ci -= a.containers; ki -= a.candles; }
      else break;
    }
    return { price: base + dp[c * W + k], atoms, singles };
  }

  const SVG = {
    container: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><ellipse cx="12" cy="6" rx="7" ry="3"/><path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6"/></svg>',
    flame: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M12 3c1 3-2 4-2 7a2.5 2.5 0 005 0c0-1 .5-1.5.5-1.5C17 12 16 18 12 21 8 18 6.5 13 8 9c.5 1 1.2 1.5 2 1.5C9 8 11 6 12 3z"/></svg>',
    tag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M3 11l8-8 9 1 1 9-8 8z"/><circle cx="15.5" cy="8.5" r="1.6" fill="currentColor" stroke="none"/></svg>',
    ship: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7.5h10v9H3z"/><path d="M13 10.5h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.7"/><circle cx="17" cy="18" r="1.7"/></svg>'
  };
  const SCENT_VAR = { renew: 'var(--sb-renew)', uplift: 'var(--sb-uplift)', unwind: 'var(--sb-unwind)' };

  class SetBuilder extends HTMLElement {
    connectedCallback() {
      // Idempotence guard: the D1 shop page moves this element between its
      // in-page home and a modal frame. Re-entry must not reset builder state.
      if (this._sbInited) return;
      this._sbInited = true;
      const cfgEl = this.querySelector('[data-set-builder-config]');
      if (!cfgEl) return;
      try { this.cfg = JSON.parse(cfgEl.textContent); }
      catch (e) { console.error('[set-builder] bad config', e); return; }

      this.pricing = normalizePricing(this.cfg.pricing);
      this.rewards = buildRewards(this.pricing, this.cfg.gifts || {});
      this.minContainers = Number(this.cfg.minContainers || 0);
      this.minCandles = Number(this.cfg.minCandles || 0);
      this.containerVariants = this.cfg.container.variants || [];
      this.candleVariants = this.cfg.candle.variants || [];
      this.stains = this.containerVariants.filter(v => v.available);

      this.state = { stainId: this.stains.length ? this.stains[0].id : null, containers: 0, candles: {}, busy: false };
      this.candleVariants.forEach(v => { this.state.candles[v.id] = 0; });
      this.seedFromUrl();
      this._seedSig = this.stateSig();

      this.buildEl = this.querySelector('[data-sb-build]');
      this.summaryEl = this.querySelector('[data-sb-summary]');
      this.mbar = this.querySelector('[data-sb-mbar]');
      this.mbarFill = this.querySelector('[data-sb-mbar-fill]');
      this.mbarTotal = this.querySelector('[data-sb-mbar-total]');
      this.mbarTogo = this.querySelector('[data-sb-mbar-togo]');
      this.mAtc = this.querySelector('[data-sb-atc-mobile]');
      this.toastEl = this.querySelector('[data-sb-toast]');
      if (!this.buildEl) return;

      this.renderBuild();
      if (this._seedScentId) this.showScentDesc(this._seedScentId);
      if (this.mAtc) this.mAtc.addEventListener('click', () => this.addToCart());
      this.update();
    }

    /* A host page swaps the scent (Noted's mood row, E.018). While the set is exactly what seeding
       produced, the new scent replaces it; once the visitor has changed anything, it adds one instead,
       so a tap never throws away a set someone built. */
    reseed(scentName) {
      const v = this.candleVariants.find(x => x.available && String(x.name).toLowerCase() === String(scentName).toLowerCase());
      if (!v || !this.buildEl) return false;
      const swap = this._seedSig === this.stateSig();
      if (swap) { this.candleVariants.forEach(x => { this.state.candles[x.id] = 0; }); this.state.candles[v.id] = 1; }
      else { this.state.candles[v.id] = (this.state.candles[v.id] || 0) + 1; }
      this.showScentDesc(v.id);
      this.update();
      if (swap) this._seedSig = this.stateSig();
      sbTrack('sb_reseed', { scent: v.name, mode: swap ? 'swap' : 'add' });
      return true;
    }
    stateSig() { return JSON.stringify([this.state.containers, this.candleVariants.map(x => this.state.candles[x.id] || 0)]); }

    /* Seed from the landing URL (email scent discs, E.014). ?scent=renew|uplift|unwind puts one
       candle of that scent in the set (a comma list puts one of each); &container=1 adds one wood
       container. Once per page, first builder only, available variants only, so a bad or stale
       link opens the builder empty, exactly as it did before this existed. */
    seedFromUrl() {
      if (window.__sbUrlSeeded) return;
      let q;
      try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
      let scents = (q.get('scent') || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean).slice(0, 3);
      let wantContainer = q.get('container') === '1';
      // A host page may name the seed instead (Noted maps its ?a= answer to a scent, E.018). The URL wins.
      const host = window.SeroBuilderSeed;
      if (!scents.length && !wantContainer && host) {
        scents = (host.scents || []).map(s => String(s).toLowerCase()).slice(0, 3);
        wantContainer = !!host.container;
      }
      if (!scents.length && !wantContainer) return;
      window.__sbUrlSeeded = true;
      const added = [];
      scents.forEach(s => {
        const v = this.candleVariants.find(x => x.available && String(x.name).toLowerCase() === s);
        if (v && !this.state.candles[v.id]) { this.state.candles[v.id] = 1; added.push(v.name); if (!this._seedScentId) this._seedScentId = v.id; }
      });
      if (wantContainer && this.stains.length) this.state.containers = 1;
      if (added.length || this.state.containers) {
        sbTrack('sb_url_seed', { scents: added.join(','), containers: this.state.containers, utm_source: q.get('utm_source') || '' });
      }
    }

    /* ---------- money ---------- */
    money(c) {
      const n = (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const f = this.cfg.moneyFormat || '${{amount}}';
      return f.indexOf('{{amount}}') > -1 ? f.replace(/\{\{\s*amount\s*\}\}/, n) : '$' + n;
    }
    dollars(c) { return +(c / 100).toFixed(2); } // numeric dollars for analytics
    money0(c) { // whole-dollar style used in the compact summary
      const n = Math.round(c / 100).toLocaleString('en-US');
      const f = this.cfg.moneyFormat || '${{amount}}';
      return f.indexOf('{{amount}}') > -1 ? f.replace(/\{\{\s*amount\s*\}\}/, n) : '$' + n;
    }

    tagHtml(t) {
      const src = this.rewardImage(t);
      // A reward with no image is not a product, so it gets a glyph that says what it is
      // rather than the generic price tag, which reads as a failed image.
      const glyph = (t.kind === 'free_shipping' || t.kind === 'free_ground') ? SVG.ship : SVG.tag;
      return src
        ? `<span class="sb-bonus__tag sb-bonus__tag--img"><img src="${src}" alt="" onerror="this.remove()"></span>`
        : `<span class="sb-bonus__tag">${glyph}</span>`;
    }

    /* ---------- derived ---------- */
    selectedStain() { return this.stains.find(v => v.id === this.state.stainId) || null; }
    candleCount() { return this.candleVariants.reduce((s, v) => s + (this.state.candles[v.id] || 0), 0); }
    itemCount() { return this.state.containers + this.candleCount(); }

    lines() {
      const out = [];
      const stain = this.selectedStain();
      if (stain && this.state.containers > 0)
        out.push({ kind: 'container', id: stain.id, name: 'Wood container', qty: this.state.containers, price: stain.price, color: 'var(--sb-fallback)', image: stain.image });
      this.candleVariants.forEach(v => {
        const q = this.state.candles[v.id] || 0;
        if (q > 0) out.push({ kind: 'candle', id: v.id, name: v.name + ' candle', qty: q, price: v.price, color: SCENT_VAR[v.name.toLowerCase()] || 'var(--sb-fallback)', image: v.image });
      });
      return out;
    }
    subtotal() { return this.lines().reduce((s, l) => s + l.price * l.qty, 0); }
    savingsInfo() {
      const sub = this.subtotal();
      if (!sub) return { savings: 0, discounted: 0, label: '' };
      const best = bestPrice(this.pricing, this.state.containers, this.candleCount());
      const savings = Math.max(0, sub - best.price);
      let label = 'Bundle savings';
      if (savings > 0 && best.atoms.length === 1 && best.singles === 0) label = best.atoms[0] + ' discount';
      return { savings, discounted: sub - savings, label };
    }
    discount() { return this.savingsInfo().savings; }
    discountedSubtotal() { return this.savingsInfo().discounted; }
    unlockedRewards() { const s = this.discountedSubtotal(); return this.rewards.filter(r => s >= r.threshold); }
    nextReward() { const s = this.discountedSubtotal(); return this.rewards.find(r => s < r.threshold) || null; }
    /* Exact-cover pricing can DROP when one more unit completes a bigger
       atom (11 candles $216 -> 12-Pack $199). Surface it, never hide it. */
    nudge() {
      const c = this.state.containers, k = this.candleCount();
      if (c + k === 0) return null;
      const cur = bestPrice(this.pricing, c, k).price;
      const plusCandle = bestPrice(this.pricing, c, k + 1).price;
      if (plusCandle < cur) return { kind: 'candle', drop: cur - plusCandle };
      const plusCont = bestPrice(this.pricing, c + 1, k).price;
      if (plusCont < cur) return { kind: 'container', drop: cur - plusCont };
      return null;
    }
    /* Seed the recommended starting composition: 1 container + 2 scents, which
       prices to exactly $89 (the locked Starter Set entry offer). Called by the
       modal host on a COLD open only, so the builder never opens at $0 with
       "start by adding containers and candles". No-op if anything is already
       chosen, so it can never overwrite a real selection. */
    preseedStarter() {
      if (this.state.containers > 0 || this.candleCount() > 0) return false;
      const avail = this.candleVariants.filter(v => v.available);
      if (!avail.length) return false;
      this.state.containers = 1;
      this.state.candles[avail[0].id] = 1;
      this.state.candles[(avail[1] || avail[0]).id] = ((avail[1] || avail[0]).id === avail[0].id) ? 2 : 1;
      this.update();
      return true;
    }
    qualifies() { return this.state.containers >= this.minContainers && this.candleCount() >= this.minCandles && this.subtotal() > 0; }

    /* ---------- gift rewards (real line items, zeroed by the discount fn) ---------- */
    giftAvailable(r) {
      if (r.kind === 'free_shipping' || r.kind === 'free_ground' || r.kind === 'bonus_candle') return true;
      const g = (this.cfg.gifts || {})[r.kind];
      return !!(g && g.available !== false && g.variantId);
    }
    rewardText(r) {
      if (!this.giftAvailable(r)) return '';
      if (r.kind === 'bonus_candle') {
        const v = this.topScentVariant();
        if (v) return r.label + ' - ' + v.name;
      }
      return r.label;
    }
    topScentVariant() { // most-added chosen scent (tie -> first); gift candle scent
      let best = null, bestQ = 0;
      this.candleVariants.forEach(v => {
        const q = this.state.candles[v.id] || 0;
        if (q > bestQ) { best = v; bestQ = q; }
      });
      return best;
    }
    rewardImage(r) { // bonus candle uses the customer's chosen scent image; hardcoded fallbacks may 404
      if (r && r.kind === 'bonus_candle') {
        const v = this.topScentVariant();
        if (v && v.image) return v.image;
      }
      return (r && r.image) || '';
    }
    giftItems(setId) { // unlocked, available gifts as real cart lines
      const items = [];
      const s = this.discountedSubtotal(); // thresholds gate on the DISCOUNTED subtotal (matches the function)
      if (!this.qualifies()) return items;
      const added = {};
      this.rewards.forEach(r => {
        if (r.kind === 'free_shipping' || r.kind === 'free_ground' || s < r.threshold || added[r.kind] || !this.giftAvailable(r)) return;
        if (r.kind === 'bonus_candle') {
          const v = this.topScentVariant();
          if (v) { items.push({ id: v.id, quantity: 1, properties: { _set_id: setId, _scent: v.name, _set_gift: 'bonus_candle' } }); added[r.kind] = 1; }
        } else {
          const g = (this.cfg.gifts || {})[r.kind];
          if (g && g.variantId) { items.push({ id: g.variantId, quantity: 1, properties: { _set_id: setId, _set_gift: r.kind } }); added[r.kind] = 1; }
        }
      });
      return items;
    }

    /* ---------- BUILD column ---------- */
    renderBuild() {
      const c = this.cfg;
      const stain0 = this.selectedStain() || this.containerVariants[0] || {};
      // K5.160b (Ben, 7 Oct): the container's thumb is the scent disc (.sb-tile__disc) in the warm
      // accent, in the same row as before; the row's ring color and glow are set in the snippet.
      const contThumb = stain0.image
        ? `<span class="sb-tile__disc"><img class="sb-tile__img" src="${stain0.image}" alt=""></span>`
        : `<span class="sb-tile__disc"><span class="sb-tile__img sb-tile__img--ph" style="background:var(--sb-fallback)">${SVG.container}</span></span>`;
      const contPrice = (this.containerVariants[0] || {}).price || 0;

      const stainsHtml = this.stains.length > 1 ? `<div class="sb-stains">${
        this.stains.map(v => `<button type="button" class="sb-stain" data-stain="${v.id}" aria-pressed="${v.id === this.state.stainId}">
          <span class="sb-stain__dot" style="background:${stainSwatch(v.name)}"></span>${escapeHtml(v.name)}</button>`).join('')
      }</div>` : '';

      const tiles = this.candleVariants.map(v => {
        const color = SCENT_VAR[v.name.toLowerCase()] || 'var(--sb-fallback)';
        const disabled = v.available ? '' : 'disabled aria-disabled="true"';
        const visual = v.image
          ? `<img class="sb-tile__img" src="${v.image}" alt="${escapeHtml(v.name)} candle" loading="lazy">`
          : `<span class="sb-tile__img sb-tile__img--ph" style="background:${color}">${SVG.flame}</span>`;
        // The button keeps its class and its place so tileFor() in shop-d1-system.liquid and
        // the modal's seeding path still find it and can still .click() it. The stepper is a
        // sibling below, not a wrapper, for the same reason.
        return `<div class="sb-tileset">
            <button type="button" class="sb-tile" data-tile="${v.id}" data-qty="0" aria-pressed="false" aria-label="Add ${escapeHtml(v.name)} candle" ${disabled}>
              <span class="sb-tile__imgwrap" style="--sb-scent:${color}"><span class="sb-tile__disc">${visual}</span></span>
              <span class="sb-tile__name">${escapeHtml(v.name)}</span>
            </button>
            <div class="sb-stepper sb-stepper--tile" data-tile-step="${v.id}">
              <button type="button" data-step="-1" aria-label="Remove one ${escapeHtml(v.name)} candle" disabled>&minus;</button>
              <output data-tile-count="${v.id}">0</output>
              <button type="button" data-step="1" aria-label="Add one ${escapeHtml(v.name)} candle" ${disabled}>+</button>
            </div>
          </div>`;
      }).join('');

      // SCENT-FIRST ORDER. Scent is what the customer came for and what the whole
      // taxonomy is built on; the container is the harder idea. Leading with a
      // $59 wood bowl stepper was the cold-start problem. Applies to the modal
      // and the embedded builder alike so the two never diverge.
      this.buildEl.innerHTML = `
        <div class="sb-candles__head">
          <h3>${escapeHtml(c.candle.headline || 'Choose your scents')}</h3>
        </div>
        <div class="sb-tiles">${tiles}</div>
        <p class="sb-tiles__desc" data-scent-desc aria-live="polite"></p>
        <div class="sb-divider"></div>
        <div class="sb-row">
          <span class="sb-thumb">${contThumb}</span>
          <div class="sb-row__info">
            <div class="sb-row__name">${escapeHtml(c.container.label)}</div>
            <div class="sb-row__price sb-row__desc">${escapeHtml(c.container.desc || '')}</div>
            <div class="sb-stepper sb-stepper--tile" data-stepper="container">
              <button type="button" data-step="-1" aria-label="Remove a container">&minus;</button>
              <output data-container-count>0</output>
              <button type="button" data-step="1" aria-label="Add a container">+</button>
            </div>
          </div>
        </div>
        ${stainsHtml}`;

      // events
      this.buildEl.querySelector('[data-stepper="container"]').addEventListener('click', e => {
        const b = e.target.closest('[data-step]'); if (!b) return;
        const step = Number(b.dataset.step);
        this.state.containers = Math.max(0, this.state.containers + step);
        this.update();
        if (step > 0) sbTrack('sb_tile_add', { item_type: 'container', qty: this.state.containers, items_total: this.itemCount(), subtotal: this.dollars(this.subtotal()) });
      });
      this.buildEl.querySelectorAll('[data-stain]').forEach(btn => btn.addEventListener('click', () => {
        this.state.stainId = Number(btn.dataset.stain);
        this.buildEl.querySelectorAll('[data-stain]').forEach(b => b.setAttribute('aria-pressed', b === btn));
        const t = this.buildEl.querySelector('.sb-thumb .sb-tile__disc'); const s = this.selectedStain();
        if (t && s && s.image) t.innerHTML = `<img class="sb-tile__img" src="${s.image}" alt="">`;
        this.update();
      }));
      // One place that changes a scent's quantity, so the tile, the stepper and the
      // summary's remove button cannot drift apart.
      this.stepCandle = (id, step) => {
        const before = this.state.candles[id] || 0;
        const q = Math.max(0, before + step);
        if (q === before) return;
        this.state.candles[id] = q;
        this.showScentDesc(id);
        this.update();
        const v = this.candleVariants.find(x => x.id === id);
        if (step > 0) sbTrack('sb_tile_add', { item_type: 'candle', scent: v ? v.name : String(id), qty: q, items_total: this.itemCount(), subtotal: this.dollars(this.subtotal()) });
      };
      this.buildEl.querySelectorAll('[data-tile]').forEach(tile => {
        if (tile.hasAttribute('disabled')) return;
        const id = Number(tile.dataset.tile);
        tile.addEventListener('click', () => this.stepCandle(id, 1));
        // Reading the three without adding one: hover on a pointer, focus on a keyboard.
        tile.addEventListener('mouseenter', () => this.showScentDesc(id));
        tile.addEventListener('focus', () => this.showScentDesc(id));
      });
      this.buildEl.querySelectorAll('[data-tile-step]').forEach(box => {
        const id = Number(box.dataset.tileStep);
        box.addEventListener('click', e => {
          const b = e.target.closest('[data-step]'); if (!b || b.disabled) return;
          this.stepCandle(id, Number(b.dataset.step));
        });
      });
    }

    /* ---------- SUMMARY column ---------- */
    renderSummary() {
      const sub = this.subtotal();
      const si = this.savingsInfo();
      const disc = si.savings;
      const final = sub - disc;
      const ds = si.discounted;
      const next = this.nextReward();
      const unlocked = this.unlockedRewards();

      // Next gift progress (thresholds gate on the DISCOUNTED subtotal)
      let bonus = '';
      if (next) {
        const prevT = unlocked.length ? unlocked[unlocked.length - 1].threshold : 0;
        const pct = ds <= prevT ? 0 : Math.min(100, ((ds - prevT) / (next.threshold - prevT)) * 100);
        const nb = this.rewardText(next) || next.label;
        bonus = `<div class="sb-bonus">
          <div class="sb-bonus__top"><span>Next bonus</span><span>${this.money0(next.threshold - ds)} to go</span></div>
          <div class="sb-bonus__main">
            ${this.tagHtml(next)}
            <div><div class="sb-bonus__label">${escapeHtml(nb)}</div><div class="sb-bonus__sub">at ${this.money0(next.threshold)}</div></div>
          </div>
          <div class="sb-bonus__bar"><div class="sb-bonus__fill" style="width:${pct}%"></div></div>
        </div>`;
      } else if (unlocked.length) {
        const top = unlocked[unlocked.length - 1];
        bonus = `<div class="sb-bonus sb-bonus--max">
          <div class="sb-bonus__top"><span>All bonuses</span><span>unlocked 🎉</span></div>
          <div class="sb-bonus__main">${this.tagHtml(top)}
            <div><div class="sb-bonus__label">${escapeHtml(unlocked.map(r => r.label).join(' + '))}</div><div class="sb-bonus__sub">Included with your set</div></div></div>
          <div class="sb-bonus__bar"><div class="sb-bonus__fill" style="width:100%"></div></div>
        </div>`;
      }

      // lines
      const lines = this.lines();
      let linesHtml = lines.map(l => `
        <li class="sb-line">
          <span class="sb-line__ic" style="background:${l.image ? '#fff' : l.color}">${l.image ? `<img src="${l.image}" alt="">` : (l.kind === 'container' ? SVG.container : SVG.flame)}</span>
          <span class="sb-line__nm">${escapeHtml(l.name)} <span class="q">×${l.qty}</span></span>
          <button type="button" class="sb-line__rm" data-rm="${l.kind}" data-id="${l.id}" aria-label="Remove one ${escapeHtml(l.name)}">&minus;</button>
          <span class="sb-line__pr">${this.money0(l.price * l.qty)}</span>
        </li>`).join('');
      // earned free bonuses (reached tiers with a non-empty bonus; gift tiers
      // only render when the gift is actually available to add for real)
      unlocked.forEach(r => {
        const txt = (r.kind === 'free_shipping' || r.kind === 'free_ground') ? r.label : this.rewardText(r);
        if (txt) linesHtml += `
          <li class="sb-line sb-line--free">
            <span class="sb-line__ic" style="background:${this.rewardImage(r) ? '#fff' : 'var(--sb-pos)'}">${this.rewardImage(r) ? `<img src="${this.rewardImage(r)}" alt="" onerror="this.remove()">` : ((r.kind === 'free_shipping' || r.kind === 'free_ground') ? SVG.ship : SVG.tag)}</span>
            <span class="sb-line__nm">${escapeHtml(txt)}</span>
            <span class="sb-line__pr">FREE</span>
          </li>`;
      });
      if (!lines.length) linesHtml = `<li class="sb-empty">Nothing in it yet. Tap a scent above.</li>`;

      let hint = 'Bonuses apply automatically at checkout.';
      const nd = this.nudge();
      if (nd) hint = `Add 1 more ${nd.kind}: your total drops ${this.money0(nd.drop)}.`;
      if (lines.length && !this.qualifies()) {
        const need = [];
        if (this.state.containers < this.minContainers) need.push(`${this.minContainers} container${this.minContainers > 1 ? 's' : ''}`);
        if (this.candleCount() < this.minCandles) need.push(`${this.minCandles} candles`);
        if (need.length) hint = `Add at least ${need.join(' and ')} to build a set.`;
      }

      this.summaryEl.innerHTML = `
        ${bonus}
        <div class="sb-label">Your set</div>
        <ul class="sb-lines">${linesHtml}</ul>
        <div class="sb-foot"${lines.length ? '' : ' hidden'}>
          ${disc > 0 ? `<div class="sb-disc"><span>${escapeHtml(si.label)}</span><span>&minus;${this.money0(disc)}</span></div>` : ''}
          <div class="sb-total"><span>Total</span><span class="sb-total__val">${disc > 0 ? `<span class="was">${this.money0(sub)}</span>` : ''}<strong>${this.money0(final)}</strong></span></div>
        </div>
        <button type="button" class="sb-atc" data-sb-atc ${this.qualifies() ? '' : 'disabled'}>
          ${this.state.busy ? 'Adding…' : 'Add to cart' + (this.itemCount() ? ' · ' + this.money0(final) : '')}
        </button>
        <p class="sb-hint">${hint}</p>`;

      this.summaryEl.querySelectorAll('[data-rm]').forEach(btn => btn.addEventListener('click', () => {
        if (btn.dataset.rm === 'container') this.state.containers = Math.max(0, this.state.containers - 1);
        else { const id = Number(btn.dataset.id); this.state.candles[id] = Math.max(0, (this.state.candles[id] || 0) - 1); }
        this.update();
      }));
      const atc = this.summaryEl.querySelector('[data-sb-atc]');
      if (atc) atc.addEventListener('click', () => this.addToCart());
    }

    /* ---------- the scent description strip ---------- */
    showScentDesc(id) {
      const el = this.buildEl.querySelector('[data-scent-desc]');
      if (!el) return;
      const v = this.candleVariants.find(x => x.id === id);
      // The description rides on the variant, so Shopify is the one place it is edited.
      const txt = (v && v.description) ? String(v.description) : '';
      if (el.textContent !== txt) el.textContent = txt;
    }

    /* ---------- shared update ---------- */
    update() {
      const cc = this.buildEl.querySelector('[data-container-count]');
      if (cc) cc.textContent = this.state.containers;
      const cMinus = this.buildEl.querySelector('[data-stepper="container"] [data-step="-1"]');
      if (cMinus) cMinus.disabled = this.state.containers === 0;
      this.candleVariants.forEach(v => {
        const q = this.state.candles[v.id] || 0;
        const tile = this.buildEl.querySelector(`[data-tile="${v.id}"]`);
        const out = this.buildEl.querySelector(`[data-tile-count="${v.id}"]`);
        const minus = this.buildEl.querySelector(`[data-tile-step="${v.id}"] [data-step="-1"]`);
        if (tile) { tile.dataset.qty = q; tile.setAttribute('aria-pressed', q > 0); }
        if (out) out.textContent = q;
        if (minus) minus.disabled = q === 0;
      });
      this.renderSummary();

      // mobile sticky bar
      const sub = this.subtotal(), disc = this.discount(), final = sub - disc, ds = sub - disc;
      const next = this.nextReward(), unlocked = this.unlockedRewards();
      if (this.mbar) {
        this.mbar.hidden = sub <= 0;
        if (this.mbarTotal) this.mbarTotal.innerHTML = (disc > 0 ? `<span class="was">${this.money0(sub)}</span>` : '') + this.money0(final);
        if (this.mbarTogo) this.mbarTogo.textContent = next ? `${this.money0(next.threshold - ds)} to ${next.label.toLowerCase()}` : (unlocked.length ? 'All bonuses unlocked 🎉' : '');
        if (this.mbarFill) { const prevT = unlocked.length ? unlocked[unlocked.length - 1].threshold : 0; this.mbarFill.style.width = next ? Math.min(100, ds <= prevT ? 0 : ((ds - prevT) / (next.threshold - prevT)) * 100) + '%' : '100%'; }
        if (this.mAtc) { this.mAtc.disabled = !this.qualifies() || this.state.busy; this.mAtc.textContent = this.state.busy ? 'Adding…' : 'Add to Cart'; }
      }

      // sb_threshold fires on gift-unlock crossings, both directions.
      // Silent on init and on post-ATC reset (resetState pre-seeds _rewardCount).
      const rc = unlocked.length;
      if (this._rewardCount === undefined) this._rewardCount = rc;
      else if (rc !== this._rewardCount) {
        sbTrack('sb_threshold', { direction: rc > this._rewardCount ? 'up' : 'down', rewards_unlocked: rc, prev_rewards_unlocked: this._rewardCount, reward: rc > this._rewardCount && unlocked.length ? unlocked[unlocked.length - 1].kind : (next ? next.kind : ''), subtotal: this.dollars(ds), items_total: this.itemCount(), currency: 'USD' });
        this._rewardCount = rc;
      }
    }

    /* ---------- add to cart ---------- */
    async addToCart() {
      if (this.state.busy || !this.qualifies()) return;
      // snapshot for sb_atc before state resets
      const sub = this.subtotal(), si = this.savingsInfo(), disc = si.savings;
      this.state.busy = true; this.update();

      const setId = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : 'set-' + Date.now();
      const items = [];
      const stain = this.selectedStain();
      if (stain && this.state.containers > 0) items.push({ id: stain.id, quantity: this.state.containers, properties: { _set_id: setId } });
      this.candleVariants.forEach(v => { const q = this.state.candles[v.id] || 0; if (q > 0) items.push({ id: v.id, quantity: q, properties: { _set_id: setId, _scent: v.name } }); });
      // Unlocked gift rewards ride along as real lines; the discount function zeroes them.
      const gifts = this.giftItems(setId);

      const post = (list) => fetch(`${this.cfg.routes.cartAdd}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: list })
      });

      try {
        let res = await post(items.concat(gifts));
        if (!res.ok && gifts.length) {
          // A gift line was rejected (e.g. out of stock). Never block the sale on a gift.
          console.warn('[set-builder] add with gifts failed; retrying without gifts');
          res = await post(items);
        }
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.description || 'Could not add to cart.'); }
        sbTrack('sb_atc', { sb_host: (this.el && this.el.closest && this.el.closest('.section-set-builder') ? (this.el.closest('.section-set-builder').getAttribute('data-sb-host') || 'embed') : 'embed'), currency: 'USD', value: this.dollars(sub - disc), subtotal: this.dollars(sub), discount: this.dollars(disc), pricing_label: si.label, items_total: this.itemCount(), containers: this.state.containers, candles: this.candleCount(), gifts: gifts.length });
        await this.refreshAndOpenCart();
        this.resetState();
      } catch (e) {
        console.error('[set-builder] add failed', e);
        this.toast(e.message || 'Something went wrong. Please try again.', null);
      } finally { this.state.busy = false; this.update(); }
    }

    resetState() { this.state.containers = 0; this.candleVariants.forEach(v => { this.state.candles[v.id] = 0; }); this._rewardCount = 0; }

    async refreshAndOpenCart() {
      // header counts (belt-and-suspenders; refreshCart usually handles these too)
      try {
        const cart = await (await fetch(`${this.cfg.routes.cart}.js`)).json();
        document.querySelectorAll('[data-header-cart-count],[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.innerHTML = cart.item_count; });
      } catch (e) {}
      // Preferred: the theme's own refresh-and-open routine (re-renders + opens #site-cart-sidebar)
      if (typeof window.refreshCart === 'function') { try { window.refreshCart(); return; } catch (e) {} }
      // Fallback: open the drawer directly
      const drawer = document.getElementById('site-cart-sidebar');
      if (drawer && typeof drawer.show === 'function') { drawer.show(); return; }
      // Last resort: in-page confirmation (no redirect)
      this.toast('Added to your cart ✓', this.cfg.routes.cart);
    }

    toast(msg, url) {
      if (!this.toastEl) return;
      this.toastEl.innerHTML = escapeHtml(msg) + (url ? ` <a href="${url}">View cart</a>` : '');
      this.toastEl.setAttribute('data-show', 'true');
      clearTimeout(this._t); this._t = setTimeout(() => this.toastEl.setAttribute('data-show', 'false'), 4500);
    }
  }

  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
  function stainSwatch(name) { return ({ Natural: '#d9c4a3', Medium: '#9a6b3f', Dark: '#4a2f1c' }[name]) || '#cdb89f'; }

  if (!customElements.get('set-builder')) customElements.define('set-builder', SetBuilder);
})();
